import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useJurisdictions, useCreateJurisdiction, useUpdateJurisdiction } from '../../hooks/useJurisdictions';
import { useAttorney } from '../../context/AttorneyContext';
import './Jurisdictions.css';

/**
 * Jurisdictions Component
 * Admin view for managing IP Office jurisdiction data
 */
function Jurisdictions() {
  const { t } = useTranslation('attorney');
  const { isAdmin } = useAttorney();
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const { data, isLoading, error, refetch } = useJurisdictions();
  const createMutation = useCreateJurisdiction();
  const updateMutation = useUpdateJurisdiction();

  const jurisdictions = data?.jurisdictions || data || [];

  const handleEdit = (jurisdiction) => {
    setSelectedJurisdiction(jurisdiction);
    setIsEditing(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedJurisdiction?.id) {
        await updateMutation.mutateAsync({
          code: selectedJurisdiction.code,
          data: formData
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setIsEditing(false);
      setSelectedJurisdiction(null);
    } catch (err) {
      console.error('Failed to save jurisdiction:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="jurisdictions-loading">
        <div className="loading-spinner" />
        <p>{t('attorney.jurisdictions.loading', 'Loading jurisdictions...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="jurisdictions-error">
        <p>{t('attorney.jurisdictions.error', 'Failed to load jurisdictions')}</p>
        <button onClick={() => refetch()}>
          {t('attorney.jurisdictions.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="jurisdictions-container">
      <div className="jurisdictions-header">
        <h1>{t('attorney.jurisdictions.title', 'Jurisdictions')}</h1>
        {isAdmin && (
          <button
            className="btn-add"
            onClick={() => {
              setSelectedJurisdiction(null);
              setIsEditing(true);
            }}
          >
            {t('attorney.jurisdictions.addNew', 'Add Jurisdiction')}
          </button>
        )}
      </div>

      <p className="jurisdictions-description">
        {t('attorney.jurisdictions.description', 'Manage IP Office information and filing portals for each jurisdiction.')}
      </p>

      {jurisdictions.length === 0 ? (
        <div className="jurisdictions-empty">
          <p>{t('attorney.jurisdictions.empty', 'No jurisdictions configured')}</p>
          {isAdmin && (
            <button
              className="btn-seed"
              onClick={async () => {
                try {
                  await fetch('/api/jurisdictions/seed', { method: 'POST' });
                  refetch();
                } catch (err) {
                  console.error('Failed to seed jurisdictions:', err);
                }
              }}
            >
              {t('attorney.jurisdictions.seedData', 'Load Default Jurisdictions')}
            </button>
          )}
        </div>
      ) : (
        <div className="jurisdictions-grid">
          {jurisdictions.map((jurisdiction) => (
            <JurisdictionCard
              key={jurisdiction.id}
              jurisdiction={jurisdiction}
              onEdit={() => handleEdit(jurisdiction)}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {isEditing && (
        <JurisdictionModal
          jurisdiction={selectedJurisdiction}
          onSave={handleSave}
          onClose={() => {
            setIsEditing(false);
            setSelectedJurisdiction(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

function JurisdictionCard({ jurisdiction, onEdit, isAdmin }) {
  const { t } = useTranslation('attorney');

  return (
    <div className="jurisdiction-card">
      <div className="jurisdiction-header">
        <div className="jurisdiction-flag">
          {getFlagEmoji(jurisdiction.code)}
        </div>
        <div className="jurisdiction-info">
          <h3 className="jurisdiction-name">{jurisdiction.name}</h3>
          <span className="jurisdiction-code">{jurisdiction.code}</span>
        </div>
        {jurisdiction.active ? (
          <span className="jurisdiction-status active">Active</span>
        ) : (
          <span className="jurisdiction-status inactive">Inactive</span>
        )}
      </div>

      <div className="jurisdiction-office">
        <span className="label">{t('attorney.jurisdictions.office', 'IP Office')}:</span>
        <span className="value">{jurisdiction.office}</span>
      </div>

      <div className="jurisdiction-portal">
        <span className="label">{t('attorney.jurisdictions.portal', 'Filing Portal')}:</span>
        {jurisdiction.portal?.filingUrl ? (
          <a href={jurisdiction.portal.filingUrl} target="_blank" rel="noopener noreferrer">
            {t('attorney.jurisdictions.openPortal', 'Open Portal')} &rarr;
          </a>
        ) : (
          <span className="no-data">Not configured</span>
        )}
      </div>

      <div className="jurisdiction-fees">
        <span className="label">{t('attorney.jurisdictions.fees', 'Fees')}:</span>
        <span className="value">
          {jurisdiction.fees?.currency} {jurisdiction.fees?.baseFee} base + {jurisdiction.fees?.perClassFee}/class
        </span>
      </div>

      <div className="jurisdiction-deadlines">
        <span className="label">{t('attorney.jurisdictions.deadlines', 'Deadlines')}:</span>
        <div className="deadline-list">
          <span>Office Action: {jurisdiction.deadlines?.officeActionResponse} days</span>
          <span>Opposition: {jurisdiction.deadlines?.oppositionPeriod} days</span>
        </div>
      </div>

      <div className="jurisdiction-contact">
        {jurisdiction.contact?.email && (
          <a href={`mailto:${jurisdiction.contact.email}`} className="contact-link">
            {jurisdiction.contact.email}
          </a>
        )}
        {jurisdiction.contact?.phone && (
          <span className="contact-phone">{jurisdiction.contact.phone}</span>
        )}
      </div>

      {isAdmin && (
        <div className="jurisdiction-actions">
          <button onClick={onEdit} className="btn-edit">
            {t('attorney.jurisdictions.edit', 'Edit')}
          </button>
        </div>
      )}
    </div>
  );
}

function JurisdictionModal({ jurisdiction, onSave, onClose, isLoading }) {
  const { t } = useTranslation('attorney');
  const isNew = !jurisdiction?.id;

  const [formData, setFormData] = useState({
    code: jurisdiction?.code || '',
    name: jurisdiction?.name || '',
    office: jurisdiction?.office || '',
    portal: {
      url: jurisdiction?.portal?.url || '',
      loginUrl: jurisdiction?.portal?.loginUrl || '',
      filingUrl: jurisdiction?.portal?.filingUrl || '',
      instructions: jurisdiction?.portal?.instructions || ''
    },
    fees: {
      baseFee: jurisdiction?.fees?.baseFee || 0,
      perClassFee: jurisdiction?.fees?.perClassFee || 0,
      currency: jurisdiction?.fees?.currency || 'USD'
    },
    deadlines: {
      officeActionResponse: jurisdiction?.deadlines?.officeActionResponse || 60,
      oppositionPeriod: jurisdiction?.deadlines?.oppositionPeriod || 30,
      renewalWindow: jurisdiction?.deadlines?.renewalWindow || 365
    },
    contact: {
      email: jurisdiction?.contact?.email || '',
      phone: jurisdiction?.contact?.phone || ''
    },
    active: jurisdiction?.active !== false
  });

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      switch (path) {
        case 'code': case 'name': case 'office': case 'active':
          newData[path] = value; break;
        case 'portal.filingUrl':
          newData.portal = { ...newData.portal, filingUrl: value }; break;
        case 'portal.instructions':
          newData.portal = { ...newData.portal, instructions: value }; break;
        case 'fees.currency':
          newData.fees = { ...newData.fees, currency: value }; break;
        case 'fees.baseFee':
          newData.fees = { ...newData.fees, baseFee: value }; break;
        case 'fees.perClassFee':
          newData.fees = { ...newData.fees, perClassFee: value }; break;
        case 'deadlines.officeActionResponse':
          newData.deadlines = { ...newData.deadlines, officeActionResponse: value }; break;
        case 'deadlines.oppositionPeriod':
          newData.deadlines = { ...newData.deadlines, oppositionPeriod: value }; break;
        case 'deadlines.renewalWindow':
          newData.deadlines = { ...newData.deadlines, renewalWindow: value }; break;
        case 'contact.email':
          newData.contact = { ...newData.contact, email: value }; break;
        case 'contact.phone':
          newData.contact = { ...newData.contact, phone: value }; break;
        default: break;
      }
      return newData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content jurisdiction-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isNew ? t('attorney.jurisdictions.addTitle', 'Add Jurisdiction') : t('attorney.jurisdictions.editTitle', 'Edit Jurisdiction')}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>{t('attorney.jurisdictions.form.code', 'Code (2 letters)')}</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                maxLength={2}
                required
                disabled={!isNew}
              />
            </div>
            <div className="form-group">
              <label>{t('attorney.jurisdictions.form.name', 'Country Name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('attorney.jurisdictions.form.office', 'IP Office Name')}</label>
            <input
              type="text"
              value={formData.office}
              onChange={(e) => handleChange('office', e.target.value)}
              required
            />
          </div>

          <fieldset className="form-fieldset">
            <legend>{t('attorney.jurisdictions.form.portalSection', 'Filing Portal')}</legend>
            <div className="form-group">
              <label>{t('attorney.jurisdictions.form.filingUrl', 'Filing URL')}</label>
              <input
                type="url"
                value={formData.portal.filingUrl}
                onChange={(e) => handleChange('portal.filingUrl', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>{t('attorney.jurisdictions.form.instructions', 'Filing Instructions')}</label>
              <textarea
                value={formData.portal.instructions}
                onChange={(e) => handleChange('portal.instructions', e.target.value)}
                rows={2}
              />
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>{t('attorney.jurisdictions.form.feesSection', 'Fees')}</legend>
            <div className="form-row">
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.currency', 'Currency')}</label>
                <select
                  value={formData.fees.currency}
                  onChange={(e) => handleChange('fees.currency', e.target.value)}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AUD">AUD</option>
                  <option value="NZD">NZD</option>
                  <option value="CAD">CAD</option>
                  <option value="SGD">SGD</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.baseFee', 'Base Fee')}</label>
                <input
                  type="number"
                  value={formData.fees.baseFee}
                  onChange={(e) => handleChange('fees.baseFee', Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.perClassFee', 'Per Class Fee')}</label>
                <input
                  type="number"
                  value={formData.fees.perClassFee}
                  onChange={(e) => handleChange('fees.perClassFee', Number(e.target.value))}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>{t('attorney.jurisdictions.form.deadlinesSection', 'Response Deadlines (days)')}</legend>
            <div className="form-row">
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.officeAction', 'Office Action')}</label>
                <input
                  type="number"
                  value={formData.deadlines.officeActionResponse}
                  onChange={(e) => handleChange('deadlines.officeActionResponse', Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.opposition', 'Opposition')}</label>
                <input
                  type="number"
                  value={formData.deadlines.oppositionPeriod}
                  onChange={(e) => handleChange('deadlines.oppositionPeriod', Number(e.target.value))}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>{t('attorney.jurisdictions.form.contactSection', 'Contact')}</legend>
            <div className="form-row">
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.email', 'Email')}</label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) => handleChange('contact.email', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('attorney.jurisdictions.form.phone', 'Phone')}</label>
                <input
                  type="tel"
                  value={formData.contact.phone}
                  onChange={(e) => handleChange('contact.phone', e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              {t('attorney.jurisdictions.form.active', 'Active')}
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              {t('attorney.jurisdictions.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={isLoading}>
              {isLoading ? t('attorney.jurisdictions.saving', 'Saving...') : t('attorney.jurisdictions.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getFlagEmoji(countryCode) {
  const flags = {
    AU: '🇦🇺',
    US: '🇺🇸',
    EU: '🇪🇺',
    UK: '🇬🇧',
    NZ: '🇳🇿',
    CA: '🇨🇦',
    SG: '🇸🇬',
    JP: '🇯🇵',
    CN: '🇨🇳',
    KR: '🇰🇷'
  };
  return flags[countryCode] || '🌍';
}

export default Jurisdictions;
